"use client";
/**
 * Shared building blocks for the Block 4 offer surfaces (inbox, home carousel,
 * checkout cross-sell, post-order card). Visual language follows the existing
 * loyalty bits + the inbox design in overview 7.5/7.6.
 */
import * as React from "react";
import { useState } from "react";
import type { TFunction } from "i18next";

import { Icons } from "@/components/Icons";
import type { OfferCard } from "@/api/services";
import type { RewardKind } from "@/lib/promotion-engine";

/* ──────── Reward-kind icon + tone ──────── */

type IconComponent = (p: { size?: number; style?: React.CSSProperties }) => React.ReactNode;

export type OfferIconMeta = { icon: IconComponent; bg: string; fg: string };

/**
 * Map a promotion's `rewardKind` (the 11 contract reward kinds — was the
 * deleted `mechanicType` union) to a glyph + colour. Conditions/window-only
 * dimensions (visit_challenge / happy_hour / member_only) are NOT reward kinds,
 * so an offer's *reward* always resolves to one of the icons; an
 * unknown / future kind falls back to a generic ticket.
 */
export function offerIconMeta(rewardKind: RewardKind | string): OfferIconMeta {
  switch (rewardKind) {
    case "free_delivery":
      return { icon: Icons.truck, bg: "var(--primary-50)", fg: "var(--primary-700)" };
    case "free_item":
    case "bundle":
    case "category_bundle":
    case "add_on":
      return { icon: Icons.gift, bg: "#fff5e2", fg: "#b4641a" };
    case "earn_multiplier":
    case "loyalty_credit":
      return { icon: Icons.spark, bg: "var(--primary-50)", fg: "var(--primary-700)" };
    case "instant_win":
      return { icon: Icons.bolt, bg: "#fbf2da", fg: "#82610f" };
    case "order_discount":
    case "item_discount":
      return { icon: Icons.tag, bg: "var(--primary-50)", fg: "var(--primary-700)" };
    case "promo_code":
    default:
      return { icon: Icons.ticket, bg: "var(--primary-50)", fg: "var(--primary-700)" };
  }
}

export function OfferIcon({
  rewardKind,
  size = 40,
  radius = 12,
  glyph = 20,
  style,
}: {
  rewardKind: RewardKind | string;
  size?: number;
  radius?: number;
  glyph?: number;
  style?: React.CSSProperties;
}) {
  const meta = offerIconMeta(rewardKind);
  const Glyph = meta.icon;
  return (
    <div
      style={{
        display: "grid",
        placeItems: "center",
        width: size,
        height: size,
        borderRadius: radius,
        background: meta.bg,
        color: meta.fg,
        flexShrink: 0,
        ...style,
      }}
    >
      <Glyph size={glyph} />
    </div>
  );
}

/* ──────── Expiry formatting ──────── */

const MS_PER_DAY = 86_400_000;

/** Whole days between now and `iso` (negative when already past). */
function daysUntil(iso: string): number | null {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.ceil((t - Date.now()) / MS_PER_DAY);
}

/**
 * Human expiry line for an active/upcoming offer, e.g. "Expires in 3 days" /
 * "Expires today" / a fallback dated string. Returns null when there's no
 * usable timestamp.
 */
export function formatExpiry(
  expiresAt: string | null | undefined,
  t: TFunction,
  lang: string,
): string | null {
  if (!expiresAt) return null;
  const days = daysUntil(expiresAt);
  if (days === null) return null;
  if (days <= 0) return t("v2.offers.expires_today");
  if (days === 1) return t("v2.offers.expires_tomorrow");
  if (days <= 14) return t("v2.offers.expires_in_days", { count: days });
  try {
    const d = new Date(expiresAt);
    const formatted = d.toLocaleDateString(lang === "ar" ? "ar-EG" : lang, {
      day: "numeric",
      month: "short",
    });
    return t("v2.offers.expires_on", { date: formatted });
  } catch {
    return null;
  }
}

/** Greyscale "Expired N days ago" line for the expired tab. */
export function formatExpiredAgo(
  expiresAt: string | null | undefined,
  t: TFunction,
): string | null {
  if (!expiresAt) return null;
  const days = daysUntil(expiresAt);
  if (days === null) return null;
  const ago = Math.max(0, -days);
  if (ago <= 0) return t("v2.offers.expired_today");
  if (ago === 1) return t("v2.offers.expired_yesterday");
  return t("v2.offers.expired_days_ago", { count: ago });
}

/** True when an active offer is within 3 days of expiry (drives "expiring" badge). */
export function isExpiringSoon(expiresAt: string | null | undefined): boolean {
  if (!expiresAt) return false;
  const days = daysUntil(expiresAt);
  return days !== null && days >= 0 && days <= 3;
}

/* ──────── Copyable code chip ──────── */

export function CodeChip({
  code,
  t,
}: {
  code: string;
  t: TFunction;
}) {
  const [copied, setCopied] = useState(false);

  const onCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard blocked — leave the code visible for manual copy.
    }
  };

  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={t("v2.offers.copy_code")}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 8,
        border: "1px dashed var(--border)",
        background: "var(--surface-2)",
        color: "var(--ink-1)",
        fontSize: 12.5,
        fontWeight: 700,
        letterSpacing: "0.04em",
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      <span dir="ltr" className="num">{code}</span>
      {copied ? (
        <Icons.check size={13} style={{ color: "var(--primary-700)" }} />
      ) : (
        <Icons.copy size={13} style={{ color: "var(--ink-3)" }} />
      )}
    </button>
  );
}

/* ──────── CTA resolution ──────── */

export type OfferCtaKind = "use_now" | "browse" | "auto_applied";

/**
 * Decide which primary CTA an offer card shows (overview 7.5):
 *  - autoApplied → passive "Auto-applied" label (no action)
 *  - has a code → "Use now" (deep-links to menu with the code pre-applied)
 *  - otherwise (category-tied) → "Browse"
 */
export function resolveOfferCta(offer: OfferCard): OfferCtaKind {
  if (offer.autoApplied) return "auto_applied";
  if (offer.code) return "use_now";
  return "browse";
}

/* ──────── Deep-link to the menu with a code pre-applied ──────── */

/**
 * Build the relative path that lands the customer on the menu with `code`
 * staged for application. HomeScreen reads `?code=` on mount and pushes the
 * promotion onto the cart via the existing `validateCode` path.
 */
export function offerUseNowPath(code: string): string {
  return `/?code=${encodeURIComponent(code)}`;
}
